import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import { type ArticleGroupFormSchema, articleGroupFormSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { ArticleGroupForm } from "@/forms/article-group-form";
import { client } from "@/hono";
import { ModalLoadFailed } from "@/components/modals";

/** Props Interface */
interface EditArticleGroupModalProps extends ModalProps {
	onComplete?: () => Promise<void>;
	articleGroupId: string;
}

export function EditArticleGroupModal({
	onClose,
	isOpen,
	onComplete,
	articleGroupId,
}: EditArticleGroupModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		const response = await client.stock["article-groups"][":articleGroupId"].$get({
			param: { articleGroupId },
		});
		const data = await responseHandler(response);
		return articleGroupFormSchema.parse({ ...data.articleGroup, autoGenerateNumber: true });
	});
	const methods = useForm({
		resolver: resolver(articleGroupFormSchema),
		defaultValues,
	});

	/** Functions */
	async function handleOnSubmit(json: ArticleGroupFormSchema) {
		const response = await client.stock["article-groups"][":articleGroupId"].$patch({
			param: { articleGroupId },
			json,
		});
		await responseHandler(response);
		await onComplete?.();
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				title={t("stock.edit_article_group")}
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading && !loadFailed}
			>
				{loadFailed ? (
					<ModalLoadFailed onClose={onClose} />
				) : (
					<>
						<Modal.Body>
							<ArticleGroupForm />
						</Modal.Body>
						<Modal.FooterForm />
					</>
				)}
			</Modal>
		</FormProvider>
	);
}
